import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  List, Clock, Eye, HandCoins, MessageSquare, Heart, PlusCircle, Trash2, Pencil, Loader2, BadgeCheck, Bookmark, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ProductCard } from "@/components/ProductCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, EmptyState } from "@/components/States";
import { LISTING_STATUS, CITIES } from "@/lib/constants";
import { LocationSelect } from "@/components/LocationSelect";
import { priceLabel, formatTRY, timeAgo } from "@/lib/format";
import { imageUrl } from "@/lib/api";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);

  const cards = [
    { label: "Aktif İlan", value: stats?.active, icon: List, color: "text-emerald-600 bg-emerald-50" },
    { label: "Bekleyen İlan", value: stats?.pending, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Toplam Görüntülenme", value: stats?.total_views, icon: Eye, color: "text-blue-600 bg-blue-50" },
    { label: "Gelen Teklif", value: stats?.offers, icon: HandCoins, color: "text-purple-600 bg-purple-50" },
    { label: "Mesaj", value: stats?.messages, icon: MessageSquare, color: "text-indigo-600 bg-indigo-50" },
    { label: "Favoriler", value: stats?.favorites, icon: Heart, color: "text-rose-600 bg-rose-50" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Merhaba, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
            {user?.account_type === "kurumsal" ? (user?.corporate_approved ? <>Onaylı Kurumsal Satıcı <BadgeCheck size={15} className="text-emerald-500" /></> : "Kurumsal hesap (onay bekliyor)") : "Bireysel hesap"}
          </p>
        </div>
        <Button asChild className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><Link to="/panel/yeni-ilan" data-testid="overview-new-listing"><PlusCircle size={18} /> Yeni İlan</Link></Button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`stat-${c.label}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${c.color}`}><c.icon size={20} /></span>
            <p className="mt-3 text-3xl font-extrabold text-slate-900">{stats ? (c.value ?? 0) : "–"}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MyListings() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/listings/mine").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("İlan silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/listings/${id}/status`, { status }); toast.success("Güncellendi"); load(); }
    catch { toast.error("Güncellenemedi"); }
  };

  if (items === null) return <LoadingState />;

  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">İlanlarım</h1>
      {items.length === 0 ? (
        <EmptyState title="Henüz ilanınız yok" description="İlk ilanınızı oluşturun ve satışa başlayın."
          action={<Button asChild className="mt-2 bg-emerald-600 hover:bg-emerald-700"><Link to="/panel/yeni-ilan">İlan Ver</Link></Button>} />
      ) : (
        <div className="space-y-3">
          {items.map((l) => {
            const st = LISTING_STATUS[l.status] || LISTING_STATUS.pasif;
            return (
              <div key={l.listing_id} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center" data-testid={`mylisting-${l.listing_id}`}>
                <Link to={`/ilan/${l.listing_id}`} className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                    {l.images?.[0] && <img src={imageUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{l.title}</p>
                    <p className="text-sm font-semibold text-emerald-900">{priceLabel(l)}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400"><Eye size={12} />{l.views || 0}</span>
                    </div>
                    {l.status === "reddedildi" && l.rejection_reason && <p className="mt-1 text-xs text-red-500">Red nedeni: {l.rejection_reason}</p>}
                  </div>
                </Link>
                <div className="flex shrink-0 items-center gap-2">
                  {l.status === "aktif" && <Button size="sm" variant="outline" onClick={() => setStatus(l.listing_id, "satildi")} data-testid={`mark-sold-${l.listing_id}`}>Satıldı</Button>}
                  <Button size="icon" variant="outline" onClick={() => navigate(`/panel/ilan-duzenle/${l.listing_id}`)} data-testid={`edit-${l.listing_id}`}><Pencil size={15} /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="icon" variant="outline" className="text-red-600" data-testid={`delete-${l.listing_id}`}><Trash2 size={15} /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>İlanı sil?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction onClick={() => remove(l.listing_id)} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function OffersReceived() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/offers/received").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const act = async (id, status) => {
    try { await api.patch(`/offers/${id}`, { status }); toast.success(status === "kabul" ? "Teklif kabul edildi" : "Teklif reddedildi"); load(); }
    catch { toast.error("İşlem başarısız"); }
  };
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Gelen Teklifler</h1>
      {items.length === 0 ? <EmptyState title="Henüz teklif yok" icon={HandCoins} /> : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.offer_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`offer-${o.offer_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={`/ilan/${o.listing_id}`} className="font-medium text-slate-800 hover:text-emerald-700">{o.listing_title}</Link>
                  <p className="text-sm text-slate-500">Teklif veren: {o.buyer?.name}</p>
                </div>
                <OfferStatusBadge status={o.status} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                <span className="text-lg font-bold text-emerald-900">{formatTRY(o.amount)}</span>
                {o.quantity != null && <span className="text-slate-500">Miktar: {o.quantity}</span>}
                <span className="ml-auto text-xs text-slate-400">{timeAgo(o.created_at)}</span>
              </div>
              {o.message && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">{o.message}</p>}
              {o.status === "beklemede" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => act(o.offer_id, "kabul")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`accept-offer-${o.offer_id}`}>Kabul Et</Button>
                  <Button size="sm" variant="outline" onClick={() => act(o.offer_id, "red")} className="text-red-600" data-testid={`reject-offer-${o.offer_id}`}>Reddet</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function OffersSent() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/offers/sent").then(({ data }) => setItems(data.items)); }, []);
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Verdiğim Teklifler</h1>
      {items.length === 0 ? <EmptyState title="Henüz teklif vermediniz" icon={HandCoins} /> : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.offer_id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
              <div><Link to={`/ilan/${o.listing_id}`} className="font-medium text-slate-800 hover:text-emerald-700">{o.listing_title}</Link>
                <p className="text-lg font-bold text-emerald-900">{formatTRY(o.amount)}</p></div>
              <OfferStatusBadge status={o.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OfferStatusBadge({ status }) {
  const map = { beklemede: ["Beklemede", "bg-amber-100 text-amber-700"], kabul: ["Kabul Edildi", "bg-emerald-100 text-emerald-700"], red: ["Reddedildi", "bg-red-100 text-red-700"] };
  const [label, cls] = map[status] || map.beklemede;
  return <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
}

export function MyBuyRequests() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/buy-requests/mine").then(({ data }) => setItems(data.items)); }, []);
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Alım Taleplerim</h1>
      {items.length === 0 ? <EmptyState title="Henüz alım talebiniz yok"
        action={<Button asChild className="mt-2 bg-emerald-600 hover:bg-emerald-700"><Link to="/alim-talepleri">Alım Talebi Oluştur</Link></Button>} /> : (
        <div className="space-y-3">
          {items.map((b) => (
            <Link key={b.buy_request_id} to={`/alim-talepleri/${b.buy_request_id}`} className="block rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm">
              <div className="flex items-center justify-between"><span className="font-semibold text-slate-800">{b.product}</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{b.responses?.length || 0} yanıt</span></div>
              <p className="mt-1 line-clamp-1 text-sm text-slate-500">{b.description}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Favorites() {
  const [items, setItems] = useState(null);
  const [favIds, setFavIds] = useState([]);
  const load = () => api.get("/favorites").then(({ data }) => { setItems(data.items); setFavIds(data.favorite_ids); });
  useEffect(() => { load(); }, []);
  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Favorilerim</h1>
      {items.length === 0 ? <EmptyState title="Favori ilanınız yok" icon={Heart} description="Beğendiğiniz ilanları kalp ikonuyla kaydedin." /> : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {items.map((l) => <ProductCard key={l.listing_id} listing={l} favored={favIds.includes(l.listing_id)} onFav={() => load()} />)}
        </div>
      )}
    </div>
  );
}

export function Profile() {
  const { user, refreshUser } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setF({
      name: user.name || "", phone: user.phone || "", city: user.city || "", district: user.district || "",
      company_name: user.company?.company_name || "", tax_office: user.company?.tax_office || "",
      tax_number: user.company?.tax_number || "", website: user.company?.website || "",
      company_description: user.company?.company_description || "",
    });
  }, [user]);

  if (!f) return <LoadingState />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", {
        name: f.name, phone: f.phone, city: f.city, district: f.district,
        company: user.account_type === "kurumsal" ? {
          company_name: f.company_name, tax_office: f.tax_office, tax_number: f.tax_number,
          website: f.website, company_description: f.company_description,
        } : null,
      });
      await refreshUser();
      toast.success("Profil güncellendi");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Profilim</h1>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Ad Soyad</Label><Input value={f.name} onChange={set("name")} data-testid="profile-name" /></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>Telefon</Label><Input value={f.phone} onChange={set("phone")} data-testid="profile-phone" /></div>
          <LocationSelect city={f.city} district={f.district}
            onChange={(city, district) => setF({ ...f, city, district })}
            cityTestId="profile-city" districtTestId="profile-district" />
        </div>
        {user.account_type === "kurumsal" && (
          <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">Firma Bilgileri
              {user.corporate_approved && <span className="flex items-center gap-1 text-emerald-600"><BadgeCheck size={14} />Onaylı</span>}</p>
            <div className="space-y-1.5"><Label>Firma Adı</Label><Input value={f.company_name} onChange={set("company_name")} data-testid="profile-company-name" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Vergi Dairesi</Label><Input value={f.tax_office} onChange={set("tax_office")} /></div>
              <div className="space-y-1.5"><Label>Vergi No</Label><Input value={f.tax_number} onChange={set("tax_number")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Web Sitesi</Label><Input value={f.website} onChange={set("website")} /></div>
            <div className="space-y-1.5"><Label>Açıklama</Label><Textarea value={f.company_description} onChange={set("company_description")} rows={2} /></div>
          </div>
        )}
        <Button onClick={save} disabled={saving} data-testid="profile-save" className="bg-emerald-600 hover:bg-emerald-700">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kaydet
        </Button>
      </div>
    </div>
  );
}

export function Settings() {
  const { user, logout } = useAuth();
  return (
    <div className="max-w-2xl">
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Hesap Ayarları</h1>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <Row label="E-posta" value={user?.email} />
        <Row label="Hesap Tipi" value={user?.account_type === "kurumsal" ? "Kurumsal" : "Bireysel"} />
        <Row label="Giriş Yöntemi" value={user?.auth_provider === "google" ? "Google" : "E-posta / Şifre"} />
        <Row label="Üyelik" value={new Date(user?.created_at).toLocaleDateString("tr-TR")} />
        <Button variant="outline" onClick={logout} className="w-full text-red-600" data-testid="settings-logout">Çıkış Yap</Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3 text-sm last:border-0">
      <span className="text-slate-500">{label}</span><span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

export function SavedSearches() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/saved-searches").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { await api.delete(`/saved-searches/${id}`); toast.success("Silindi"); load(); };

  const buildUrl = (q = {}) => {
    const sp = new URLSearchParams();
    ["q", "category", "city", "condition", "seller_type", "unit", "min_price", "max_price", "harvest_status"].forEach((k) => { if (q[k]) sp.set(k, q[k]); });
    const base = q.is_agriculture === "true" ? "/tarlada-kalanlar" : q.is_wholesale === "true" ? "/toptan-firsatlar" : "/ilanlar";
    return `${base}?${sp.toString()}`;
  };

  const chips = (q = {}) => Object.entries(q)
    .filter(([k]) => !["is_agriculture", "is_wholesale"].includes(k))
    .map(([k, v]) => v);

  if (items === null) return <LoadingState />;
  return (
    <div>
      <h1 className="mb-5 font-display text-2xl font-extrabold text-slate-900">Kayıtlı Aramalar</h1>
      {items.length === 0 ? (
        <EmptyState title="Kayıtlı aramanız yok" icon={Bookmark}
          description="İlanlar sayfasında filtre uygulayıp 'Kaydet' ile arama oluşturun. Uygun yeni ilanlarda bildirim alırsınız." />
      ) : (
        <div className="space-y-3">
          {items.map((ss) => (
            <div key={ss.saved_search_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`saved-search-${ss.saved_search_id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800">{ss.name}</p>
                <p className="truncate text-xs text-slate-500">{chips(ss.query).join(" • ") || "Tüm ilanlar"}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button asChild size="sm" variant="outline" className="gap-1"><Link to={buildUrl(ss.query)} data-testid={`view-search-${ss.saved_search_id}`}><Search size={14} /> Görüntüle</Link></Button>
                <Button size="icon" variant="outline" className="text-red-600" onClick={() => remove(ss.saved_search_id)} data-testid={`delete-search-${ss.saved_search_id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
